import React, { useState } from 'react';
import { map } from 'lodash';
import styled from '@emotion/styled';
import { AspectRatios, Image } from '@model/common/image';
import { withTheme } from '@emotion/react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps, ClickableThemeProps } from '@theme/base';
import { Icon } from '@components/common/generic-icon';
import { useI18NextContext } from '@components/hooks';
import { mq } from '@styles/breakpoints';
import { InfoCardModal } from '@components/common/modal';
import { ImageCrossfade } from '@components/common/image-crossfade';
import { RoomTypeComponentVariant } from '@model/fit-product';
import { Room } from '@model/iceberg/deal-finder/deal-finder';
import { mixins } from '@styles/mixins';

/* ***************** *
 *       Types       *
 * ***************** */

export interface RoomTypeTileProps {
  theme?: Theme;
  testId?: string;
  roomType: Room;
  gallery: Array<Image>;
  description: string;
  variant?: RoomTypeComponentVariant;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container: any = styled.div(({ theme, isClickable }: ClickableThemeProps) => ({
  ...mixins(theme).card,
  cursor: isClickable ? 'pointer' : 'default',
  height: '100%',

  [mq.small]: {
    width: 'calc(100% - 4px)',
    height: 'calc(100% - 4px)'
  }
}));

const RoomImageContainer: any = styled.div({
  cursor: 'pointer'
});

const TileContentContainer: any = styled.div(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.medium,
  paddingBottom: theme.custom.spacing.medium
}));

const Title: any = styled.h5(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  margin: 0,
  color: theme.custom.colors.group4.base,
  transition: `color ${theme.custom.transitions.default}`
}));

const IconsRow = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.custom.spacing.xSmall,
  marginBottom: theme.custom.spacing.xSmall,
  ...(theme.custom.typography.labels.attribute as any)
}));

const RoomAttribute = styled.span(({ theme }: ThemeProps) => ({
  marginRight: theme.custom.spacing.medium,

  ['span']: {
    paddingLeft: theme.custom.spacing.xSmall
  }
}));

const roomAttributeIcon = (theme: Theme) => ({
  color: theme.custom.colors.group4.light,
  fontSize: '1.5rem'
});

const RoomDetailsModalLink: any = styled.a(({ theme }: ThemeProps) => ({
  cursor: 'pointer',
  ...(theme.custom.typography.labels.default as any)
}));

/* *************************** *
 *    RoomTypeTileComponent    *
 * *************************** */

export const RoomTypeTileComponent = (props: RoomTypeTileProps) => {
  const t: any = useI18NextContext();
  const { roomType, theme, gallery, description } = props;

  /* *** STATE *** */
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [isMouseOver, setIsMouseOver] = useState<boolean>(false);

  /* *** LOGIC *** */

  const handleMoreRoomDetails = (event: Event) => {
    event.stopPropagation();
    setShowDetailsModal(true);
  };

  const hasRoomImages = !!gallery?.length;

  return (
    <>
      <Container
        {...((description || !!gallery?.length) && { onClick: handleMoreRoomDetails })}
        isClickable={description || !!gallery?.length}
        data-testid={props.testId || TestId.roomTypes.roomTypeTile.main}
        onMouseEnter={() => setIsMouseOver(true)}
        onMouseLeave={() => setIsMouseOver(false)}
      >
        {hasRoomImages && (
          <RoomImageContainer onClick={handleMoreRoomDetails}>
            <ImageCrossfade images={map(gallery, 'url')} triggerPlay={isMouseOver} aspectRatio={AspectRatios['16:9']} />
          </RoomImageContainer>
        )}
        <TileContentContainer>
          <Title data-testid={TestId.roomTypes.roomTypeTile.title}>{roomType.roomType}</Title>
          <IconsRow>
            {/* NOTE: IconsRow is static for now as we don't have the data contract agreed for it */}
            {!!roomType.squareMetres && roomType.squareMetres > 0 && (
              <RoomAttribute>
                <Icon name={'mhi-size'} css={roomAttributeIcon(theme!)} />
                <span>
                  {roomType.squareMetres}m<sup>2</sup>
                </span>
              </RoomAttribute>
            )}
            {!!roomType.wifi && (
              <RoomAttribute>
                <Icon name={'mhi-wifi'} css={roomAttributeIcon(theme!)} />
                <span>
                  {roomType.wifi} {t('product__room-types--attributes-wifi')}
                </span>
              </RoomAttribute>
            )}
          </IconsRow>
          {(description || !!gallery?.length) && (
            <RoomDetailsModalLink onClick={handleMoreRoomDetails}>
              {t('product__room-types--view-room-details')}
            </RoomDetailsModalLink>
          )}
        </TileContentContainer>
      </Container>
      {(description || !!gallery?.length) && (
        <InfoCardModal
          title={roomType.roomType}
          show={showDetailsModal}
          onDismiss={() => setShowDetailsModal(false)}
          gallery={gallery.map(({ url }: Image) => url)}
          description={description}
          showGalleryDots
        />
      )}
    </>
  );
};

export const RoomTypeTile = withTheme(RoomTypeTileComponent);
